import { createStore } from "vuex";
import cloudloop from "../mixins/cloudloop";
import createPersistedState from "vuex-persistedstate";
import { clearRecent } from "../kit/recentSearches.js";

export default createStore({
  plugins: [
    createPersistedState({
      paths: ["loggedInUser"],
    }),
  ],
  state: {
    loggedInUser: null,
    selectedSession: null,
    selectedProfile: null,
    showDialog: false,
    stagedNewSessionAudio: [],
    stagedNewSessionArchive: null,
    // Usernames New session invites once the session exists (W4's "+ New
    // Session with Maya…").
    newSessionInvitees: [],
    searchQuery: "",
    searchResults: { users: [], sessions: [] },
    files: [],
    invitations: [],
    inboxCount: 0,
    // Short-lived signed media tokens for the current session's loops, from
    // /api/media/session-urls. Empty until a session loads, and empty whenever
    // signing is disabled server-side — the URL builders then omit the token,
    // which is exactly the pre-signing behavior.
    mediaTokens: { loops: {}, library_token: null, als_token: null, signing_enabled: false },
    // Hashes of loops the current user has starred (from /api/starred_loops),
    // loaded when a session opens so each card can seed its star.
    starredLoops: [],
  },

  getters: {
    filteredFiles: (state) => (status) => {
      return state.files.filter((file) => file.status === status);
    },
    mediaStreamToken: (state) => (hash) => {
      const entry = state.mediaTokens && state.mediaTokens.loops && state.mediaTokens.loops[hash];
      return (entry && entry.stream_token) || null;
    },
    mediaDownloadToken: (state) => (hash) => {
      const entry = state.mediaTokens && state.mediaTokens.loops && state.mediaTokens.loops[hash];
      return (entry && entry.download_token) || null;
    },
    mediaLibraryToken: (state) => (state.mediaTokens && state.mediaTokens.library_token) || null,
    mediaAlsToken: (state) => (state.mediaTokens && state.mediaTokens.als_token) || null,
    isLoopStarred: (state) => (hash) =>
      Array.isArray(state.starredLoops) && state.starredLoops.includes(hash),
  },

  mutations: {
    setShowDialog(state, value) {
      state.showDialog = value;
    },

    setStagedNewSessionAudio(state, files) {
      state.stagedNewSessionAudio = Array.isArray(files) ? files : [];
    },

    clearStagedNewSessionAudio(state) {
      state.stagedNewSessionAudio = [];
    },

    setStagedNewSessionArchive(state, file) {
      state.stagedNewSessionArchive = file || null;
    },

    clearStagedNewSessionArchive(state) {
      state.stagedNewSessionArchive = null;
    },

    setNewSessionInvitees(state, usernames) {
      state.newSessionInvitees = Array.isArray(usernames) ? usernames.filter((u) => typeof u === "string" && u) : [];
    },

    setLoggedInUser(state, user) {
      state.loggedInUser = user;
    },

    // Signed out: forget everything that belonged to the last account, so a
    // page left open never shows it (a profile's email, an inbox, tokens).
    clearUserData(state) {
      state.selectedProfile = null;
      state.selectedSession = null;
      state.invitations = [];
      state.inboxCount = 0;
      state.starredLoops = [];
      state.newSessionInvitees = [];
      state.searchQuery = "";
      state.searchResults = { users: [], sessions: [] };
      state.mediaTokens = { loops: {}, library_token: null, als_token: null, signing_enabled: false };
      // Search's recent searches were theirs too (S9).
      clearRecent();
    },

    setSelectedSession(state, value) {
      state.selectedSession = value;
    },

    setMediaTokens(state, value) {
      state.mediaTokens = value || {
        loops: {},
        library_token: null,
        als_token: null,
        signing_enabled: false,
      };
    },

    setStarredLoops(state, hashes) {
      state.starredLoops = Array.isArray(hashes) ? hashes : [];
    },
    addStarredLoop(state, hash) {
      if (!state.starredLoops.includes(hash)) state.starredLoops.push(hash);
    },
    removeStarredLoop(state, hash) {
      state.starredLoops = state.starredLoops.filter((h) => h !== hash);
    },

    addFile(state, file) {
      state.files.push(file);
    },

    setSelectedProfile(state, profile) {
      state.selectedProfile = profile;
    },

    updateFile(state, payload) {
      const index = state.files.findIndex((file) => file.uuid === payload.uuid);
      if (index !== -1) {
        state.files[index] = payload.newFile;
      }
    },
    
    updateFileProgress(state, payload) {
      const index = state.files.findIndex((file) => file.uuid === payload.uuid);
      if (index !== -1) {
        state.files[index].progress = payload.progress;
      }
    },
    
    updateFileStatus(state, payload) {
      const index = state.files.findIndex((file) => file.uuid === payload.uuid);
      if (index !== -1) {
        state.files[index].status = payload.status;
      }
    },

    removeFile(state, uuid) {
      const index = state.files.findIndex((file) => file.uuid === uuid);
      if (index !== -1) {
        const [removed] = state.files.splice(index, 1);
        if (removed.fileUrl) URL.revokeObjectURL(removed.fileUrl);
      }
    },
    setSearchQuery(state, query) {
      state.searchQuery = query;
    },
    clearSearch(state) {
      state.searchQuery = "";
    },
    setInvitations(state, invitations) {
      state.invitations = invitations;
      state.inboxCount = invitations.length;
    },
    setInboxCount(state, count) {
      state.inboxCount = count;
    },
    addInvitation(state, invitation) {
      state.invitations.unshift(invitation);
      state.inboxCount = state.invitations.length;
    },
    removeInvitation(state, invitationId) {
      state.invitations = state.invitations.filter((i) => i.id !== invitationId);
      state.inboxCount = state.invitations.length;
    },
  },

  actions: {
    setShowDialog({ commit }, payload) {
      commit("setShowDialog", payload);
    },
    stageNewSessionAudio({ commit }, files) {
      commit("setStagedNewSessionAudio", files);
      commit("setShowDialog", true);
    },
    stageNewSessionArchive({ commit }, file) {
      commit("setStagedNewSessionArchive", file);
      commit("setShowDialog", true);
    },
    // New session with these people invited once it exists.
    stageNewSessionInvitees({ commit }, usernames) {
      commit("setNewSessionInvitees", usernames);
      commit("setShowDialog", true);
    },
    // Star or unstar a sound: the shared list flips now and flips back if
    // the server refuses (POST|DELETE /loops/<h>/star).
    toggleStar({ commit, state }, { hash } = {}) {
      if (!hash) return Promise.resolve(false);
      const was = Array.isArray(state.starredLoops) && state.starredLoops.includes(hash);
      commit(was ? "removeStarredLoop" : "addStarredLoop", hash);
      const call = was ? cloudloop.methods.unstarLoop(hash) : cloudloop.methods.starLoop(hash);
      return call
        .then(() => !was)
        .catch(() => {
          commit(was ? "addStarredLoop" : "removeStarredLoop", hash);
          return was;
        });
    },
    // Signed in: the starred sounds, so every list shows its stars without
    // opening a session first.
    loadStarred({ commit, state }) {
      if (!state.loggedInUser) return Promise.resolve([]);
      return cloudloop.methods
        .fetchStarredLoops()
        .then((res) => {
          const hashes = (res && res.data && res.data.results) || [];
          commit("setStarredLoops", hashes);
          return hashes;
        })
        .catch(() => state.starredLoops);
    },
    clearSearch({ commit }) {
      commit("clearSearch");
    },
    setSearchQuery({ commit }, payload) {
      commit("setSearchQuery", payload);
    },
    setSelectedSession({ commit }, payload) {
      commit("setSelectedSession", payload.session);
    },

    selectUserProfile({ commit }, payload) {
      commit("setSelectedProfile", payload.user);
    },

    logInUser({ dispatch }, userPass) {
      return cloudloop.methods
        .logInUser(userPass.username, userPass.password)
        .then((data) => {
          // localStorage.setItem is synchronous. Resolve only after
          // fetchUser commits loggedInUser — the router guard treats a
          // null loggedInUser as unauthenticated and would bounce the
          // post-login navigation back to the login screen.
          window.localStorage.setItem("JWT", data.data.results);
          return dispatch("fetchUser", userPass.username);
        });
    },

    fetchUserProfile({ commit }, username) {
      return new Promise((resolve, reject) => {
        cloudloop.methods
          .fetchUser(username)
          .then(({ data: { results } }) => {
            commit("setSelectedProfile", results);
            resolve();
          })
          .catch(reject);
      });
    },

    fetchUser({ commit }, username) {
      return new Promise((resolve, reject) => {
        cloudloop.methods
          .fetchUser(username)
          .then(({ data: { results } }) => {
            commit("setLoggedInUser", results);
            resolve();
          })
          .catch(reject);
      });
    },

    // Log out always succeeds locally. The server is told first (it needs
    // the token to revoke it), then the token, the user and their data are
    // forgotten whatever it answered. The old version committed the user
    // away and rejected synchronously before the server replied, so callers
    // never navigated and the JWT stayed in storage.
    logOutUser({ commit }) {
      return cloudloop.methods
        .logOutUser()
        .catch(() => {
          /* offline or already invalid: still sign out here */
        })
        .then(() => {
          try {
            window.localStorage.removeItem("JWT");
          } catch (e) {
            /* storage unavailable */
          }
          commit("setLoggedInUser", null);
          commit("clearUserData");
        });
    },

    // The account is gone server side, so there is nothing to log out of:
    // just forget the token and the user, like logOutUser does locally.
    accountDeleted({ commit }) {
      try {
        window.localStorage.removeItem("JWT");
      } catch (e) {
        /* storage unavailable */
      }
      commit("setLoggedInUser", null);
      commit("setSelectedProfile", null);
    },

    registerUser({ commit, dispatch }, userEmailPass) {
      return new Promise((resolve, reject) => {
        cloudloop.methods
          .registerUser(
            userEmailPass.username,
            userEmailPass.email,
            userEmailPass.password,
            userEmailPass.referredBy || null,
            userEmailPass.acceptTerms === true,
          )
          .then((data) => {
            console.log("Registered user!" + data);
            if (data && data.data && data.data.results) {
              window.localStorage.setItem("JWT", data.data.results);
              return dispatch("fetchUser", userEmailPass.username)
                .then(() => resolve(data))
                .catch(reject);
            }
            commit("setLoggedInUser", null);
            resolve(data);
          })
          .catch(reject);
      });
    },

    fetchInbox({ commit }) {
      return cloudloop.methods
        .fetchInbox()
        .then(({ data: { results } }) => {
          commit("setInvitations", results.results || results);
        })
        .catch((err) => console.error("Failed to fetch inbox:", err));
    },

    fetchInboxCount({ commit }) {
      return cloudloop.methods
        .fetchInboxCount()
        .then(({ data: { results } }) => {
          commit("setInboxCount", results.count || 0);
        })
        .catch((err) => console.error("Failed to fetch inbox count:", err));
    },

    acceptInvitation({ commit }, invitationId) {
      return cloudloop.methods
        .acceptInvitation(invitationId)
        .then(({ data: { results } }) => {
          commit("removeInvitation", invitationId);
          if (results) {
            commit("setSelectedSession", results);
          }
          return results;
        });
    },

    declineInvitation({ commit }, invitationId) {
      return cloudloop.methods
        .declineInvitation(invitationId)
        .then(() => {
          commit("removeInvitation", invitationId);
        });
    },
  },

  modules: {},
});
